/**
 * <SharesEmptyState> + <SharesLoadingSkeleton> — feature-shared chrome.
 *
 * `external-sharing` Phase 3 Session 3.4 (2026-05-09).
 *
 * Centralizes the empty / error / loading visual variants that both
 * `<SentTab>` (Phase 3) and `<ReceivedTab>` (Phase 4) consume, so the two
 * tabs stay visually in sync without duplicating the EmptyState
 * composition. Returns null OR a single `<EmptyState>` element — never a
 * loading state on the same component (loading is a sibling primitive).
 */
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';

export type SharesEmptyKind =
  | { kind: 'sent-empty' }
  | { kind: 'received-empty' }
  | { kind: 'sent-error'; onRetry: () => void }
  | { kind: 'received-error'; onRetry: () => void };

export interface SharesEmptyStateProps {
  variant: SharesEmptyKind;
}

export function SharesEmptyState({ variant }: SharesEmptyStateProps) {
  if (variant.kind === 'sent-empty') {
    return (
      <div data-testid="shares-sent-empty">
        <EmptyState
          title="NO SHARES YET"
          description="Share your first deal from the Builder."
          action={
            <Link to="/builder" search={{ tab: 'workbench' as const }}>
              <Button variant="primary">Open Builder</Button>
            </Link>
          }
        />
      </div>
    );
  }
  if (variant.kind === 'received-empty') {
    return (
      <div data-testid="shares-received-empty">
        <EmptyState
          title="NO SHARED DEALS"
          description="Deals other Arcanum users share with you appear here."
        />
      </div>
    );
  }
  if (variant.kind === 'sent-error') {
    return (
      <div data-testid="shares-sent-error">
        <EmptyState
          title="COULDN'T LOAD SHARES"
          description="Network error or API unreachable."
          action={
            <Button
              variant="secondary"
              onClick={variant.onRetry}
              data-testid="shares-sent-retry"
            >
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  // received-error
  return (
    <div data-testid="shares-received-error">
      <EmptyState
        title="COULDN'T LOAD SHARES"
        description="Network error or API unreachable."
        action={
          <Button
            variant="secondary"
            onClick={variant.onRetry}
            data-testid="shares-received-retry"
          >
            Retry
          </Button>
        }
      />
    </div>
  );
}

export interface SharesLoadingSkeletonProps {
  /** `data-testid` to disambiguate Sent vs Received (each tab has its own
   *  walkthrough fixture). */
  testid: string;
  /** Number of row-shaped placeholders. Default 3 — matches the typical
   *  initial-render row count for a small inbox. */
  rows?: number;
}

export function SharesLoadingSkeleton({
  testid,
  rows = 3,
}: SharesLoadingSkeletonProps) {
  return (
    <div data-testid={testid} className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="block" className="h-16" />
      ))}
    </div>
  );
}
